import React from 'react';

import { ChatsProvider, useChats } from '@context/ChatsContext';
import { ContactsProvider } from '@context/ContactsContext';
import { FollowingProvider } from '@context/FollowingContext';
import { GroupsProvider } from '@context/GroupsContext';
import { useNotifications } from '@context/NotificationsContext';
import { NotificationsProvider } from '@context/NotificationsContext';
import { RecommendationsProvider } from '@context/RecommendationsContext';
import { RequestsProvider } from '@context/RequestsContext';
import {
  CurrentUserProvider,
  OnboardingStatusProvider,
  useOnboardingStatus,
} from '@context/UsersContext';
import { logError } from '@helpers/Loggers';
import { setBadgeCount } from '@helpers/Notifications';
import { AppErrorScreen, AppLoadingScreen } from '@navigation/AppGateScreens';
import OnboardedStack from '@navigation/OnboardedStack';
import OnboardingStack from '@navigation/OnboardingStack';
import { OnboardingRoute } from '@navigation/Routes';

export default function AuthenticatedStack() {
  return (
    <OnboardingStatusProvider>
      <AppProviders>
        <Stack />
      </AppProviders>
    </OnboardingStatusProvider>
  );
}

function AppProviders({ children }: React.PropsWithChildren) {
  return (
    // Note: some providers depend on parent providers, order matters!
    <CurrentUserProvider>
      <RequestsProvider>
        <ChatsProvider>
          <GroupsProvider>
            <NotificationsProvider>
              <ContactsProvider>
                <FollowingProvider>
                  <RecommendationsProvider>{children}</RecommendationsProvider>
                </FollowingProvider>
              </ContactsProvider>
            </NotificationsProvider>
          </GroupsProvider>
        </ChatsProvider>
      </RequestsProvider>
    </CurrentUserProvider>
  );
}

function Stack() {
  const { onboarded, loading, userDocExists, error, errorProps } = useOnboardingStatus();
  const { unreadNotificationCount } = useNotifications();
  const { unreadMessageCount } = useChats();

  React.useEffect(() => {
    setBadgeCount(unreadNotificationCount + unreadMessageCount).catch((error) => logError(error));
  }, [unreadNotificationCount, unreadMessageCount]);

  if (error) {
    return <AppErrorScreen errorProps={errorProps} />;
  }

  if (loading) {
    return <AppLoadingScreen />;
  }

  if (onboarded) {
    return <OnboardedStack />;
  }

  return (
    <OnboardingStack
      // start at user private form if user doc already exists
      initialRouteName={
        userDocExists ? OnboardingRoute.UserPrivateForm : OnboardingRoute.CreateUser
      }
    />
  );
}
